import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from "recharts";
import { Download } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useApi } from "@/hooks/useApi";
import { apiClient } from "@/api/client";
import type { Deal, DealStatus, BillingRecord } from "@/types";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { PRICING_ENABLED } from "@/lib/pricing";
import { PendingRequestsPanel } from "@/components/PendingRequestsPanel";
import { ScopeBadge } from "@/components/ScopeBadge";

// ── Local types ────────────────────────────────────────────────────────────────

interface AdminUser {
  user_id: string;
  email: string;
  name: string;
  role: string;
  firm_name: string | null;
  last_login: string | null;
  created_at: string | null;
  status: string;
}

interface BugRecord {
  bug_id: string;
  title: string;
  severity: string;
  status: string;
  source: string | null;
  page: string | null;
  reported_by: string | null;
  assigned_to: string | null;
  created_at: string | null;
}

type TabKey = "revenue" | "deals" | "users" | "requests" | "bugs";

// ── Helpers ────────────────────────────────────────────────────────────────────

function fmt(n: number | null | undefined): string {
  if (n == null) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency", currency: "USD", maximumFractionDigits: 0,
  }).format(n);
}

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-US", {
    month: "short", day: "numeric", year: "numeric",
  });
}

function fmtMonth(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", { month: "short", year: "2-digit" });
}

const ROLE_BADGE: Record<string, string> = {
  super_admin:      "bg-purple-100 text-purple-900 border-purple-300",
  cpa:              "bg-blue-50 text-blue-800 border-blue-200",
  analyst:          "bg-sky-100 text-sky-900 border-sky-300",
  firm:             "bg-gray-100 text-gray-700 border-gray-300",
  pending_approval: "bg-amber-100 text-amber-800 border-amber-300",
};

const SEV_BADGE: Record<string, string> = {
  critical: "bg-red-100 text-red-900 border-red-300",
  high:     "bg-orange-100 text-orange-900 border-orange-300",
  medium:   "bg-amber-100 text-amber-800 border-amber-300",
  low:      "bg-gray-100 text-gray-600 border-gray-300",
};

const BUG_STATUS_BADGE: Record<string, string> = {
  open:        "bg-red-100 text-red-900 border-red-300",
  in_progress: "bg-blue-100 text-blue-900 border-blue-300",
  resolved:    "bg-green-100 text-green-900 border-green-300",
  wont_fix:    "bg-gray-100 text-gray-600 border-gray-300",
};

const SOURCE_BADGE: Record<string, string> = {
  customer:  "bg-indigo-100 text-indigo-800 border-indigo-300",
  log_scan:  "bg-slate-100 text-slate-700 border-slate-300",
  internal:  "bg-teal-100 text-teal-800 border-teal-300",
};

function RoleBadge({ role }: { role: string }) {
  return (
    <Badge className={`border text-xs ${ROLE_BADGE[role] ?? "bg-gray-100 text-gray-600"}`}>
      {role.replace(/_/g, " ")}
    </Badge>
  );
}

const STATUS_LABELS: Record<DealStatus, string> = {
  setup: "Setup", seller_intake: "Seller Intake", in_analysis: "In Analysis",
  report_ready: "Report Ready", delivered: "Delivered", closed: "Closed", withdrawn: "Withdrawn",
};

const STATUS_LIST: DealStatus[] = [
  "setup", "seller_intake", "in_analysis", "report_ready", "delivered", "closed", "withdrawn",
];

// ── Animation ─────────────────────────────────────────────────────────────────

const cardVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.3 } },
};

const containerVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.07 } },
};

// ── KpiCard ────────────────────────────────────────────────────────────────────

function KpiCard({ title, value, subtitle }: {
  title: string; value: string | number; subtitle?: string;
}) {
  return (
    <motion.div variants={cardVariants}>
      <Card>
        <CardHeader className="pb-1">
          <CardTitle className="text-sm font-medium text-muted-foreground">{title}</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-2xl font-bold" style={{ color: "var(--color-primary)" }}>{value}</p>
          {subtitle && <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>}
        </CardContent>
      </Card>
    </motion.div>
  );
}

// ── Revenue tab ────────────────────────────────────────────────────────────────

function RevenueTab() {
  // GET /qoe/billing returns { records, count } — an object, not a bare array
  // (apps/api/routers/qoe/reports.py). Typing it as BillingRecord[] made
  // records.filter(...) throw a TypeError and white-screened /admin (SWEEP-FIX-1).
  type BillingResponse = { records: BillingRecord[]; count: number };
  const { data: billing, loading, execute } = useApi<BillingResponse>();

  useEffect(() => {
    execute(() =>
      apiClient.get<BillingResponse>("/qoe/billing")
        .then(r => r.data)
        .catch(() => ({ records: [], count: 0 } as BillingResponse))
    );
  }, [execute]);

  const records = billing?.records ?? [];
  const thisMonth = new Date().toISOString().slice(0, 7);
  const thisYear  = new Date().getFullYear().toString();

  const mtdRevenue = records
    .filter(r => (r as BillingRecord & { created_at?: string }).created_at?.startsWith(thisMonth))
    .reduce((s, r) => s + (r.fee ?? 0), 0);
  const ytdRevenue = records
    .filter(r => (r as BillingRecord & { created_at?: string }).created_at?.startsWith(thisYear))
    .reduce((s, r) => s + (r.fee ?? 0), 0);
  const avgDealSize = records.length > 0
    ? records.reduce((s, r) => s + (r.fee ?? 0), 0) / records.length
    : 0;
  const reportsMTD = records.filter(
    r => r.status === "paid" &&
      (r as BillingRecord & { created_at?: string }).created_at?.startsWith(thisMonth)
  ).length;

  const monthlyData = useMemo(() => {
    const months: Record<string, { month: string; total: number; yansar: number; partner: number }> = {};
    for (const r of records) {
      const created = (r as BillingRecord & { created_at?: string }).created_at;
      if (!created) continue;
      const key = created.slice(0, 7);
      if (!months[key]) {
        months[key] = { month: fmtMonth(created), total: 0, yansar: 0, partner: 0 };
      }
      months[key].total   += r.fee ?? 0;
      months[key].yansar  += r.yansar_share ?? 0;
      months[key].partner += Math.max(0, (r.fee ?? 0) - (r.yansar_share ?? 0));
    }
    return Object.entries(months)
      .sort(([a], [b]) => a.localeCompare(b))
      .slice(-12)
      .map(([, v]) => v);
  }, [records]);

  const exportCSV = () => {
    const header = "ID,Business,Firm,Fee,Yansar Share,Status\n";
    const rows = records.map(r =>
      `${r.billing_id},"${r.business_name}","${r.firm ?? ""}",${r.fee ?? 0},${r.yansar_share ?? 0},${r.status}`
    ).join("\n");
    const blob = new Blob([header + rows], { type: "text/csv" });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement("a");
    a.href = url;
    a.download = "billing_export.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      <motion.div
        className="grid grid-cols-2 lg:grid-cols-4 gap-4"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <KpiCard title="MTD Revenue"            value={loading ? "—" : fmt(mtdRevenue)} />
        <KpiCard title="YTD Revenue"            value={loading ? "—" : fmt(ytdRevenue)} />
        <KpiCard title="Avg Deal Size"          value={loading ? "—" : fmt(avgDealSize)} />
        <KpiCard title="Reports Delivered MTD"  value={loading ? "—" : reportsMTD} />
      </motion.div>

      {monthlyData.length > 0 && (
        <Card>
          <CardHeader><CardTitle>Monthly Revenue (last 12 months)</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={monthlyData} margin={{ top: 4, right: 16, left: 0, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                <YAxis tickFormatter={v => `$${(Number(v) / 1000).toFixed(0)}k`} tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v) => fmt(Number(v))} />
                <Legend />
                <Bar dataKey="total"   name="Total Fees"      fill="var(--color-primary)" />
                <Bar dataKey="yansar"  name="Yansar Share"    fill="#22c55e" />
                <Bar dataKey="partner" name="Partner Payouts" fill="#94a3b8" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Billing Records</CardTitle>
            <Button size="sm" variant="outline" onClick={exportCSV}>
              <Download className="mr-1.5 h-3.5 w-3.5" />
              Export CSV
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="space-y-2 p-4">
              {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-9 w-full" />)}
            </div>
          ) : records.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">No billing records.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Business</TableHead>
                  <TableHead>Firm</TableHead>
                  <TableHead className="text-right">Fee</TableHead>
                  <TableHead className="text-right">Yansar Share</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {records.map(r => (
                  <TableRow key={r.billing_id}>
                    <TableCell className="font-medium">{r.business_name}</TableCell>
                    <TableCell className="text-muted-foreground">{r.firm ?? "—"}</TableCell>
                    <TableCell className="text-right tabular-nums">{fmt(r.fee)}</TableCell>
                    <TableCell className="text-right tabular-nums">{fmt(r.yansar_share)}</TableCell>
                    <TableCell>
                      <Badge variant={
                        r.status === "paid" ? "default"
                          : r.status === "invoiced" ? "secondary"
                          : "outline"
                      }>
                        {r.status}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ── Deals tab ──────────────────────────────────────────────────────────────────

function DealsTab() {
  const navigate = useNavigate();
  const { data, loading, execute } = useApi<{ deals: Deal[] }>();
  const [firmFilter, setFirmFilter]     = useState("");
  const [statusFilter, setStatusFilter] = useState<DealStatus | "">("");

  useEffect(() => {
    execute(() =>
      apiClient.get<{ deals: Deal[] }>("/admin/deals")
        .then(r => r.data)
        .catch(() => ({ deals: [] }))
    );
  }, [execute]);

  const deals = data?.deals ?? [];

  const firms = useMemo(() => {
    const set = new Set(deals.map(d => d.firm ?? "").filter(Boolean));
    return Array.from(set).sort();
  }, [deals]);

  const filtered = useMemo(() => {
    let rows = deals;
    if (firmFilter)   rows = rows.filter(d => d.firm === firmFilter);
    if (statusFilter) rows = rows.filter(d => d.status === statusFilter);
    return rows;
  }, [deals, firmFilter, statusFilter]);

  const SELECT = "h-8 rounded-lg border border-input bg-transparent px-2 text-sm outline-none";

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
          <CardTitle>All Deals</CardTitle>
          <div className="flex gap-2">
            <select value={firmFilter} onChange={e => setFirmFilter(e.target.value)} className={SELECT}>
              <option value="">All firms</option>
              {firms.map(f => <option key={f} value={f}>{f}</option>)}
            </select>
            <select
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value as DealStatus | "")}
              className={SELECT}
            >
              <option value="">All statuses</option>
              {STATUS_LIST.map(s => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
            </select>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Business</TableHead>
              <TableHead>Firm</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Scope</TableHead>
              <TableHead>Analyst</TableHead>
              <TableHead>Created</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              Array.from({ length: 4 }).map((_, i) => (
                <TableRow key={i}>
                  {Array.from({ length: 6 }).map((_, j) => (
                    <TableCell key={j}><Skeleton className="h-4 w-full" /></TableCell>
                  ))}
                </TableRow>
              ))
            ) : filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="py-10 text-center text-muted-foreground">
                  No deals match the current filters.
                </TableCell>
              </TableRow>
            ) : (
              filtered.map(deal => (
                <TableRow
                  key={deal.deal_id}
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => navigate(`/normalization?deal_id=${deal.deal_id}`)}
                >
                  <TableCell className="font-medium">{deal.business_name}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{deal.firm ?? "—"}</TableCell>
                  <TableCell>
                    <Badge variant="outline">{STATUS_LABELS[deal.status] ?? deal.status}</Badge>
                  </TableCell>
                  <TableCell>
                    <ScopeBadge
                      tier={(deal as Deal & { scope_tier?: "lite" | "focused" | "full" }).scope_tier}
                      unconfigured={(deal as Deal & { scope_score?: number | null }).scope_score == null}
                    />
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">{deal.analyst ?? "—"}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{fmtDate(deal.created_at)}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

// ── Users tab ──────────────────────────────────────────────────────────────────

function UsersTab() {
  const { data, loading, execute } = useApi<{ users: AdminUser[] }>();
  const [deactivateTarget, setDeactivateTarget] = useState<AdminUser | null>(null);
  const [deactivating, setDeactivating] = useState(false);

  useEffect(() => {
    execute(() =>
      apiClient.get<{ users: AdminUser[] }>("/v1/admin/users")
        .then(r => r.data)
        .catch(() => ({ users: [] }))
    );
  }, [execute]);

  const users = data?.users ?? [];

  const handleDeactivate = async () => {
    if (!deactivateTarget) return;
    setDeactivating(true);
    try {
      await apiClient.patch(`/v1/admin/users/${deactivateTarget.user_id}`, { active: false });
      toast.success(`${deactivateTarget.name} deactivated`);
      setDeactivateTarget(null);
    } catch {
      toast.error("Deactivation not yet implemented");
    } finally {
      setDeactivating(false);
    }
  };

  return (
    <>
      <Card>
        <CardHeader><CardTitle>Platform Users</CardTitle></CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Firm</TableHead>
                <TableHead>Last Login</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                Array.from({ length: 4 }).map((_, i) => (
                  <TableRow key={i}>
                    {Array.from({ length: 6 }).map((_, j) => (
                      <TableCell key={j}><Skeleton className="h-4 w-full" /></TableCell>
                    ))}
                  </TableRow>
                ))
              ) : users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="py-10 text-center text-muted-foreground">
                    No users found.
                  </TableCell>
                </TableRow>
              ) : (
                users.map(u => (
                  <TableRow key={u.user_id}>
                    <TableCell className="font-medium">{u.name}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{u.email}</TableCell>
                    <TableCell><RoleBadge role={u.role} /></TableCell>
                    <TableCell className="text-xs text-muted-foreground">{u.firm_name ?? "—"}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{fmtDate(u.last_login)}</TableCell>
                    <TableCell>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 text-xs text-destructive border-destructive/30 hover:bg-destructive/5"
                        onClick={() => setDeactivateTarget(u)}
                      >
                        Deactivate
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog
        open={!!deactivateTarget}
        onOpenChange={open => { if (!open) setDeactivateTarget(null); }}
      >
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Deactivate User</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Are you sure you want to deactivate{" "}
            <span className="font-semibold text-foreground">{deactivateTarget?.name}</span>?
            They will no longer be able to log in.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeactivateTarget(null)}>Cancel</Button>
            <Button variant="destructive" disabled={deactivating} onClick={handleDeactivate}>
              {deactivating ? "Deactivating…" : "Deactivate"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ── Bugs tab ───────────────────────────────────────────────────────────────────

function BugsTab() {
  const { data, loading, execute } = useApi<{ bugs: BugRecord[] }>();
  const [statusFilter, setStatusFilter] = useState("");

  const fetchBugs = useCallback(() => {
    const params = statusFilter ? `?status=${statusFilter}` : "";
    execute(() =>
      apiClient.get<{ bugs: BugRecord[] }>(`/bugs${params}`)
        .then(r => r.data)
        .catch(() => ({ bugs: [] }))
    );
  }, [execute, statusFilter]);

  useEffect(() => { fetchBugs(); }, [fetchBugs]);

  const bugs = data?.bugs ?? [];
  const SELECT = "h-8 rounded-lg border border-input bg-transparent px-2 text-sm outline-none";

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>Bug Tracker</CardTitle>
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className={SELECT}>
            <option value="">All statuses</option>
            <option value="open">Open</option>
            <option value="in_progress">In Progress</option>
            <option value="resolved">Resolved</option>
            <option value="wont_fix">Won't Fix</option>
          </select>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>ID</TableHead>
              <TableHead>Title</TableHead>
              <TableHead>Severity</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Source</TableHead>
              <TableHead>Page</TableHead>
              <TableHead>Reported By</TableHead>
              <TableHead>Created</TableHead>
              <TableHead>Assigned To</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              Array.from({ length: 4 }).map((_, i) => (
                <TableRow key={i}>
                  {Array.from({ length: 9 }).map((_, j) => (
                    <TableCell key={j}><Skeleton className="h-4 w-full" /></TableCell>
                  ))}
                </TableRow>
              ))
            ) : bugs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} className="py-10 text-center text-muted-foreground">
                  No bugs found.
                </TableCell>
              </TableRow>
            ) : (
              bugs.map(b => (
                <TableRow key={b.bug_id}>
                  <TableCell className="font-mono text-xs text-muted-foreground">{b.bug_id}</TableCell>
                  <TableCell className="max-w-[180px] truncate font-medium">{b.title}</TableCell>
                  <TableCell>
                    <Badge className={`border text-xs ${SEV_BADGE[b.severity] ?? "bg-gray-100 text-gray-600"}`}>
                      {b.severity}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge className={`border text-xs ${BUG_STATUS_BADGE[b.status] ?? "bg-gray-100 text-gray-600"}`}>
                      {b.status.replace(/_/g, " ")}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {b.source && (
                      <Badge className={`border text-xs ${SOURCE_BADGE[b.source] ?? "bg-gray-100 text-gray-600"}`}>
                        {b.source.replace(/_/g, " ")}
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">{b.page ?? "—"}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{b.reported_by ?? "—"}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{fmtDate(b.created_at)}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{b.assigned_to ?? "—"}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

// ── Tab registry ───────────────────────────────────────────────────────────────

const ALL_TABS: { key: TabKey; label: string }[] = [
  { key: "revenue",  label: "Revenue"          },
  { key: "deals",    label: "Deals"            },
  { key: "users",    label: "Users"            },
  { key: "requests", label: "Pending Requests" },
  { key: "bugs",     label: "Bugs"             },
];

// Revenue is a pure fee/$ surface — dropped entirely when QOE_PRICING_ENABLED is off.
const TABS = ALL_TABS.filter(t => t.key !== "revenue" || PRICING_ENABLED);

// ── AdminDashboard ─────────────────────────────────────────────────────────────

export function AdminDashboard() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<TabKey>(PRICING_ENABLED ? "revenue" : "deals");

  if (user?.role !== "super_admin") {
    return (
      <div className="p-6">
        <Alert variant="destructive">
          <AlertDescription>Admin access is restricted to super_admin role.</AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 max-w-7xl mx-auto">
      <div>
        <h1 className="text-2xl font-bold" style={{ color: "var(--color-primary)" }}>
          Admin Dashboard
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Platform-wide view — revenue, deals, users, and bug tracker
        </p>
      </div>

      {/* Tab bar */}
      <div className="flex gap-1 border-b">
        {TABS.map(t => (
          <button
            key={t.key}
            onClick={() => setActiveTab(t.key)}
            className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px ${
              activeTab === t.key
                ? "border-[--color-primary] text-[--color-primary]"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {activeTab === "revenue"  && PRICING_ENABLED && <RevenueTab />}
      {activeTab === "deals"    && <DealsTab />}
      {activeTab === "users"    && <UsersTab />}
      {activeTab === "requests" && (
        <Card>
          <CardHeader><CardTitle>Pending Access Requests</CardTitle></CardHeader>
          <CardContent className="p-0">
            <PendingRequestsPanel />
          </CardContent>
        </Card>
      )}
      {activeTab === "bugs" && <BugsTab />}
    </div>
  );
}

export default AdminDashboard;
